import React, { useState, useEffect } from "react";
import Icon from "../../../components/AppIcon";
import Button from "../../../components/ui/Button";
import Input from "../../../components/ui/Input";
import Select from "../../../components/ui/Select";
import { useAuth } from "../../../contexts/AuthContext";

const TeamsTab = ({
  teams,
  clusters,
  teamMembers,
  onSaveTeam,
  onDeleteTeam,
}) => {
  const { user } = useAuth();
  const [isAddingTeam, setIsAddingTeam] = useState(false);
  const [editingTeamId, setEditingTeamId] = useState(null);
  const [teamForm, setTeamForm] = useState({
    id: null,
    name: "",
    cluster_id: "",
    user_ids: [],
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [selectedClusterFilter, setSelectedClusterFilter] = useState("all");

  useEffect(() => {
    if (isAddingTeam && !editingTeamId) {
      const selectedProject = localStorage.getItem("selectedProject");
      const projectId =
        selectedProject && selectedProject !== "all"
          ? selectedProject
          : user?.project_id || null;
      // Auto-select first cluster if available
      const projectClusters =
        clusters?.filter((c) => c.project_id === projectId) || [];
      setTeamForm({
        id: null,
        name: "",
        cluster_id: projectClusters[0]?.id || "",
        user_ids: [],
      });
    }
  }, [isAddingTeam, editingTeamId, user, clusters]);

  const clusterOptions =
    clusters?.map((cluster) => ({
      value: cluster.id,
      label: cluster.name,
    })) || [];

  const filteredClusters =
    selectedClusterFilter === "all"
      ? clusters
      : clusters?.filter((c) => c.id === selectedClusterFilter) || [];

  const filteredTeams =
    selectedClusterFilter === "all"
      ? teams
      : teams?.filter((t) => t.cluster_id === selectedClusterFilter) || [];

  const userOptions = (teamMembers || [])
    .filter((member) => member.category === "staff")
    .map((member) => ({
      value: member.id,
      label: `${member.unique_id} - ${member.name || "Unnamed"}`,
    }));

  const validateForm = () => {
    const newErrors = {};
    if (!teamForm?.name?.trim()) {
      newErrors.name = "Team name is required";
    }
    if (!teamForm?.cluster_id) {
      newErrors.cluster_id = "Cluster is required";
    }
    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    if (!onSaveTeam) return;
    if (!validateForm()) return;

    try {
      setLoading(true);
      await onSaveTeam({
        id: teamForm.id,
        name: teamForm.name,
        cluster_id: teamForm.cluster_id,
        user_ids: teamForm.user_ids || [],
      });
      setTeamForm({
        id: null,
        name: "",
        cluster_id: "",
        user_ids: [],
      });
      setErrors({});
      setIsAddingTeam(false);
      setEditingTeamId(null);
    } catch (err) {
      // Error handling is done in parent component
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (teamId) => {
    if (
      window.confirm(
        "Are you sure you want to delete this team? This will remove all team memberships."
      )
    ) {
      try {
        setDeletingId(teamId);
        await onDeleteTeam(teamId);
      } catch (err) {
        // Error handling is done in parent component
      } finally {
        setDeletingId(null);
      }
    }
  };

  const handleEdit = (team) => {
    // Get members directly from teamMembers where team_id matches
    const membersForTeam = (teamMembers || []).filter(
      (member) => member.team_id === team.id
    );
    setTeamForm({
      id: team.id,
      name: team.name || "",
      cluster_id: team.cluster_id || "",
      user_ids: membersForTeam.map((member) => member.id),
    });
    setEditingTeamId(team.id);
    setIsAddingTeam(false);
  };

  const handleCancel = () => {
    setIsAddingTeam(false);
    setEditingTeamId(null);
    setTeamForm({
      id: null,
      name: "",
      cluster_id: "",
      user_ids: [],
    });
    setErrors({});
  };

  const getTeamMembers = (teamId) => {
    // Get members directly where team_id matches
    return (teamMembers || []).filter((member) => member.team_id === teamId);
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="min-w-0">
          <h3 className="text-lg font-semibold text-foreground">Teams</h3>
          <p className="text-sm text-muted-foreground">
            Manage teams within clusters
          </p>
        </div>
        <Button
          variant="default"
          iconName="Plus"
          iconPosition="left"
          onClick={() => {
            setIsAddingTeam(true);
            setEditingTeamId(null);
            setTeamForm({
              id: null,
              name: "",
              cluster_id: "",
              user_ids: [],
            });
            setErrors({});
          }}
        >
          Add Team
        </Button>
      </div>

      {/* Filter */}
      <div className="bg-card border border-border rounded-lg p-4">
        <Select
          label="Filter by Cluster"
          options={[{ value: "all", label: "All Clusters" }, ...clusterOptions]}
          value={selectedClusterFilter}
          onChange={(value) => setSelectedClusterFilter(value)}
          searchable
        />
      </div>

      {/* Add / Edit Modal */}
      {(isAddingTeam || editingTeamId) && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[2000] p-4">
          <div className="bg-card rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-6 border-b border-border">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                  <Icon name="UserCog" size={20} className="text-primary" />
                </div>
                <div>
                  <h2 className="text-xl font-semibold text-foreground">
                    {editingTeamId ? "Edit Team" : "Add Team"}
                  </h2>
                </div>
              </div>
              <button
                onClick={handleCancel}
                className="w-8 h-8 rounded-lg hover:bg-muted flex items-center justify-center"
                disabled={loading}
              >
                <Icon name="X" size={20} />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              <Select
                label="Cluster"
                options={clusterOptions}
                value={teamForm.cluster_id}
                onChange={(value) => {
                  setTeamForm({ ...teamForm, cluster_id: value });
                  if (errors?.cluster_id)
                    setErrors({ ...errors, cluster_id: "" });
                }}
                error={errors?.cluster_id}
                placeholder="Select cluster"
                required
                searchable
              />

              <Input
                label="Team Name"
                type="text"
                placeholder="e.g., Team Alpha, Team Beta"
                value={teamForm.name}
                onChange={(e) => {
                  setTeamForm({ ...teamForm, name: e.target.value });
                  if (errors?.name) setErrors({ ...errors, name: "" });
                }}
                error={errors?.name}
                required
              />

              <Select
                label="Team Members (Optional)"
                options={userOptions}
                value={teamForm.user_ids}
                onChange={(value) => {
                  setTeamForm({ ...teamForm, user_ids: value || [] });
                }}
                multiple
                searchable
                placeholder="Select team members (optional)"
                description="Optionally select one or more staff members to be part of this team."
              />

              <div className="flex items-center justify-end gap-3 pt-4 border-t border-border">
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleCancel}
                  disabled={loading}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  variant="default"
                  loading={loading}
                  disabled={loading}
                >
                  {editingTeamId ? "Update" : "Create"}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Teams List */}
      <div className="bg-card border border-border rounded-lg overflow-hidden">
        {filteredTeams?.length > 0 ? (
          <div className="divide-y divide-border">
            {filteredTeams.map((team) => {
              const cluster = clusters?.find((c) => c.id === team.cluster_id);
              const members = getTeamMembers(team.id);
              return (
                <div
                  key={team.id}
                  className="p-4 hover:bg-muted/50 transition-colors"
                >
                  <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-3 mb-2">
                        <Icon
                          name="UserCog"
                          size={20}
                          className="text-primary flex-shrink-0"
                        />
                        <h4 className="text-base font-semibold text-foreground truncate">
                          {team.name}
                        </h4>
                      </div>
                      <div className="flex flex-wrap items-center gap-2 md:gap-4 text-sm text-muted-foreground ml-8">
                        <div className="flex items-center gap-2">
                          <Icon name="MapPin" size={14} className="flex-shrink-0" />
                          <span className="truncate">{cluster?.name || "N/A"}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <Icon name="Users" size={14} className="flex-shrink-0" />
                          <span>
                            {members.length} Member
                            {members.length !== 1 ? "s" : ""}
                          </span>
                        </div>
                      </div>
                    </div>
                    <div className="flex flex-shrink-0 items-center gap-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        iconName="Edit2"
                        onClick={() => handleEdit(team)}
                        disabled={deletingId === team.id}
                      >
                        Edit
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        iconName="Trash2"
                        onClick={() => handleDelete(team.id)}
                        loading={deletingId === team.id}
                        disabled={deletingId === team.id}
                        className="text-destructive hover:text-destructive"
                      >
                        Delete
                      </Button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="p-12 text-center">
            <Icon
              name="UserCog"
              size={48}
              className="mx-auto mb-4 text-muted-foreground"
            />
            <p className="text-sm text-muted-foreground">
              {selectedClusterFilter === "all"
                ? "No teams added yet. Click 'Add Team' to create one."
                : "No teams in this cluster."}
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default TeamsTab;
